import React, { useState } from 'react';
import { Upload, Button, Image, message, Spin } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { uploadService } from '@/services/upload';
import type { UploadFile as UploadFileType } from '@/services/upload/types';

interface ImageUploadProps {
    value?: string;
    onChange?: (url: string) => void;
    folder?: string;
    maxSize?: number; // MB
    accept?: string;
    disabled?: boolean;
    placeholder?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
    value,
    onChange,
    folder = 'brands',
    maxSize = 5, // 5MB default
    accept = 'image/*',
    disabled = false,
    placeholder = 'Chọn ảnh để upload'
}) => {
    const [uploading, setUploading] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);

    const handleUpload = async (file: File) => {
        // Validate file size
        if (file.size > maxSize * 1024 * 1024) {
            message.error(`File không được vượt quá ${maxSize}MB!`);
            return false;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            message.error('Chỉ chấp nhận file ảnh!');
            return false;
        }

        setUploading(true);
        try {
            // Convert File to UploadFile format for web
            const uploadFile: UploadFileType = {
                name: file.name,
                type: file.type,
                uri: file, // Pass the actual File object
            };

            const response = await uploadService.uploadSingle(uploadFile, { folder });

            if (response.file_url) {
                message.success(response.message || 'Upload ảnh thành công!');
                onChange?.(response.file_url);
            } else {
                message.error(response.message || 'Upload ảnh thất bại!');
            }
        } catch (error: any) {
            console.error('Upload error:', error);
            message.error('Có lỗi xảy ra khi upload ảnh!');
        } finally {
            setUploading(false);
        }

        return false; // Prevent default upload behavior
    };

    const handleRemove = () => {
        onChange?.('');
    };

    const uploadProps = {
        beforeUpload: handleUpload,
        showUploadList: false,
        accept,
        disabled: disabled || uploading,
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {value ? (
                <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: 12,
                    backgroundColor: '#fafafa'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 8
                    }}>
                        <span style={{ fontSize: 14, color: '#666' }}>Ảnh đã upload:</span>
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={handleRemove}
                            disabled={disabled}
                        >
                            Xóa
                        </Button>
                    </div>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <Image
                            src={value}
                            alt="Preview"
                            style={{
                                maxWidth: 200,
                                maxHeight: 200,
                                objectFit: 'cover',
                                borderRadius: 6,
                                border: '1px solid #d9d9d9'
                            }}
                            preview={{
                                visible: previewVisible,
                                onVisibleChange: setPreviewVisible,
                            }}
                        />
                    </div>
                </div>
            ) : null}

            <Upload {...uploadProps}>
                <Button
                    icon={<UploadOutlined />}
                    loading={uploading}
                    disabled={disabled}
                    style={{ width: '100%' }}
                >
                    {uploading ? 'Đang upload...' : placeholder}
                </Button>
            </Upload>

            {uploading && (
                <div style={{ textAlign: 'center', marginTop: 8 }}>
                    <Spin size="small" />
                    <span style={{ marginLeft: 8 }}>Đang upload...</span>
                </div>
            )}

            <div style={{ fontSize: 12, color: '#999' }}>
                Hỗ trợ: JPG, PNG, GIF, WebP (tối đa {maxSize}MB)
            </div>
        </div>
    );
};

export default ImageUpload;
